'use client';

import React from 'react';
import styled from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';
import type { DefaultTheme } from 'styled-components';

export type ToastTone = 'info' | 'success' | 'warn' | 'error';

export interface ToastProps {
  tone?: ToastTone;
  icon?: React.ReactNode;
  /** When provided, renders a dismiss "×" button. */
  onDismiss?: () => void;
  children: React.ReactNode;
  className?: string;
}

const toneAccent = (tone: ToastTone, theme: DefaultTheme) => {
  const map = {
    info: theme.colors.semantic.info,
    success: theme.colors.semantic.success,
    warn: theme.colors.semantic.warn,
    error: theme.colors.semantic.error,
  } as const;
  return map[tone];
};

const Surface = styled.div<{ $tone: ToastTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.space[2.5]};
  padding: ${(p) => p.theme.space[2.5]} ${(p) => p.theme.space[3.5]};
  background: ${(p) => p.theme.colors.neutral.ink};
  color: ${(p) => p.theme.colors.neutral.bg};
  border-radius: ${(p) => p.theme.radius.lg};
  box-shadow: ${(p) => p.theme.elevation.floating.shadow};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  line-height: 1.4;
  max-width: 420px;
  border-left: 3px solid ${(p) => toneAccent(p.$tone, p.theme).color};
`;

const IconSlot = styled.span<{ $tone: ToastTone }>`
  display: inline-flex;
  flex-shrink: 0;
  color: ${(p) => toneAccent(p.$tone, p.theme).color};
`;

const Body = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const DismissButton = styled.button`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border-radius: ${(p) => p.theme.radius.sm};
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }
  ${focusRing}
`;

/**
 * Transient floating notification — dark surface, tone-accent stripe,
 * optional icon and dismiss. This is the visual primitive only; for a
 * fully-managed toast queue with auto-dismiss timers and stacking, build
 * a `ToastProvider` consuming this component (separate work).
 */
export function Toast({ tone = 'info', icon, onDismiss, children, className }: ToastProps) {
  const role = tone === 'error' ? 'alert' : 'status';
  return (
    <Surface role={role} $tone={tone} className={className}>
      {icon !== undefined && <IconSlot $tone={tone}>{icon}</IconSlot>}
      <Body>{children}</Body>
      {onDismiss !== undefined && (
        <DismissButton type="button" onClick={onDismiss} aria-label="Dismiss">
          ×
        </DismissButton>
      )}
    </Surface>
  );
}
